import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Modal from '../../components/ui/Modal';
import { CheckCircle, XCircle, Clock, DollarSign, ArrowDown, ArrowUp, Search, Eye, Image } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const STATUSES = ['pending', 'approved', 'rejected', 'all'];

function RequestPanel({ type, title, icon: Icon, color }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchRequests(); }, [status, search]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = `/api/admin/transactions/${type}s?limit=100&status=${status === 'all' ? '' : status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await api.get(url);
      setRequests(res.data.items || res.data.requests || []);
    } catch { toast.error(`Failed to load ${type}s`); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/api/admin/transactions/${id}/approve`, { admin_notes: adminNotes });
      toast.success(`${type} approved!`);
      setSelected(null); setAdminNotes(''); fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const handleReject = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/api/admin/transactions/${id}/reject`, { reason: adminNotes });
      toast.success(`${type} rejected`);
      setSelected(null); setAdminNotes(''); fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const resolveUrl = (url) => url?.startsWith('http') ? url : `${API_BASE}${url}`;

  return (
    <div className="glass-card rounded-xl p-4 space-y-3" data-testid={`${type}-requests-panel`}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Icon className={`w-4 h-4 text-${color}-400`} /> {title}
          {requests.filter(r => r.status === 'pending').length > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
              {requests.filter(r => r.status === 'pending').length} pending
            </span>
          )}
        </h3>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="flex rounded-lg p-0.5 bg-[#0B0E14]">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded-md text-[10px] font-medium capitalize transition ${status === s ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'}`}>
              {s}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by username or email..."
            className="h-7 text-xs pl-8 bg-[#0B0E14] border-white/10 rounded-lg" data-testid={`${type}-search`} />
        </div>
      </div>

      {/* Request List */}
      {loading ? <div className="flex justify-center py-4"><div className="spinner" /></div> : requests.length === 0 ? (
        <p className="text-center text-gray-500 py-4 text-xs">No {type} requests found</p>
      ) : (
        <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
          {requests.map(req => (
            <div key={req.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-white/3 transition"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }} data-testid={`request-${req.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: type === 'deposit' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)' }}>
                  {type === 'deposit' ? <ArrowDown className="w-4 h-4 text-green-400" /> : <ArrowUp className="w-4 h-4 text-red-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-white truncate">{req.username || 'Unknown'}</span>
                    <span className="text-[9px] text-gray-500">{req.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-gray-500">
                    <span>{req.payment_method_name || 'N/A'}</span>
                    <span>{new Date(req.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className={`text-sm font-bold ${type === 'deposit' ? 'text-green-400' : 'text-red-400'}`}>${req.amount}</p>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                    req.status === 'approved' || req.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                    req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
                  }`}>{req.status}</span>
                </div>
                {req.status === 'pending' && (
                  <div className="flex gap-1">
                    <button onClick={() => { setSelected(req); setAdminNotes(''); }} className="p-1.5 rounded-lg hover:bg-white/5" data-testid={`view-${req.id}`}>
                      <Eye className="w-4 h-4 text-gray-400" />
                    </button>
                    <button onClick={() => handleApprove(req.id)} className="p-1.5 rounded-lg hover:bg-green-500/10" data-testid={`approve-${req.id}`}>
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    </button>
                    <button onClick={() => handleReject(req.id)} className="p-1.5 rounded-lg hover:bg-red-500/10" data-testid={`reject-${req.id}`}>
                      <XCircle className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title={`${type} Request Details`} maxWidth="max-w-md">
        {selected && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[10px] text-gray-500">User</p><p className="font-medium">{selected.username}</p></div>
              <div><p className="text-[10px] text-gray-500">Amount</p><p className="font-bold text-[#D4AF37]">${selected.amount}</p></div>
              <div><p className="text-[10px] text-gray-500">Method</p><p>{selected.payment_method_name || 'N/A'}</p></div>
              <div><p className="text-[10px] text-gray-500">Status</p><p className="capitalize">{selected.status}</p></div>
              <div><p className="text-[10px] text-gray-500">Date</p><p>{new Date(selected.created_at).toLocaleString()}</p></div>
            </div>
            {selected.proof_image_url && (
              <div>
                <p className="text-[10px] text-gray-500 mb-1">Payment Proof</p>
                <img src={resolveUrl(selected.proof_image_url)} alt="Proof" className="w-full rounded-lg max-h-64 object-contain bg-black/30" />
              </div>
            )}
            {selected.notes && <div><p className="text-[10px] text-gray-500">Notes</p><p className="text-sm">{selected.notes}</p></div>}
            {selected.payout_details && (
              <div><p className="text-[10px] text-gray-500">Payout Details</p><pre className="text-xs bg-[#0B0E14] p-2 rounded-lg overflow-auto">{JSON.stringify(selected.payout_details, null, 2)}</pre></div>
            )}
            {selected.status === 'pending' && (
              <>
                <Input value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Admin notes (optional)" className="bg-[#0B0E14] border-white/10" />
                <div className="flex gap-2">
                  <Button onClick={() => handleApprove(selected.id)} disabled={processing} className="flex-1 bg-green-600 hover:bg-green-700">
                    {processing ? '...' : 'Approve'}
                  </Button>
                  <Button onClick={() => handleReject(selected.id)} disabled={processing} className="flex-1 bg-red-600 hover:bg-red-700">
                    {processing ? '...' : 'Reject'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function TransactionsPage() {
  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="transactions-page">
      <h2 className="text-xl font-semibold">Payment Requests</h2>
      <RequestPanel type="deposit" title="Deposit Requests" icon={ArrowDown} color="green" />
      <RequestPanel type="withdrawal" title="Withdrawal Requests" icon={ArrowUp} color="red" />
    </div>
  );
}
